import { useCallback, useEffect, useState } from 'react';
import { hasMoreSpecificNavigationMatch } from '@/shared/lib/navigation-match';
import { NavLink, useLocation } from 'react-router-dom';
import {
  ChevronDown,
  CircleHelp,
  Truck,
  Workflow,
  Settings2,
  ChartNoAxesCombined,
} from 'lucide-react';

import { FEATURES } from '@/app/config/features';
import { useTranslation } from '@/app/providers';
import { ROUTES } from '@/app/router/route-constants';
import { useAppDispatch, useAppSelector } from '@/app/store/hooks';
import { selectMobileMenuOpen, setMobileMenuOpen } from '@/app/store/ui-slice';
import { Icon, type IconComponent } from '@/shared/components/Icon';
import { BRAND } from '@/shared/constants/brand';
import { cn } from '@/shared/lib/cn';
import { BranchIndicator } from '@/widgets/branch-indicator/branch-indicator';
import { LanguageSelector } from '@/widgets/language-selector/language-selector';
import { NavDropdown } from '@/widgets/nav-dropdown/nav-dropdown';
import { ProfileDropdown } from '@/widgets/profile-dropdown/profile-dropdown';
import { ThemeToggle } from '@/widgets/theme-toggle/theme-toggle';

import {
  NavDeliveryRiderIcon,
  NavCalendarIcon,
  NavCollaboratorsIcon,
  NavConfigureWhatsAppIcon,
  NavEventsIcon,
  NavExtraServicesIcon,
  NavFidelityIcon,
  NavFormDataCollectionIcon,
  NavFormIcon,
  NavHomeIcon,
  NavLandingPagesIcon,
  NavLocalConfigurationIcon,
  NavMarketingIcon,
  NavMassiveDispatchIcon,
  NavMenuIcon,
  NavMessagesIcon,
  NavMyClientsIcon,
  NavOfficialWhatsAppChannelIcon,
  NavOthersIcon,
  NavPaymentsIcon,
  NavPromotionsIcon,
  NavPublicRelationIcon,
  NavReportCollaboratorsIcon,
  NavReservationsIcon,
  NavSettingsIcon,
  NavStaffIcon,
  NavStripeConfigurationIcon,
  NavStripeTransactionsIcon,
  NavTagIcon,
  NavTemplatesIcon,
  NavWhatsAppOfficialIcon,
} from './nav-icons';

type NavLabelKey =
  | 'nav.dashboard'
  | 'nav.calendar'
  | 'nav.bookingHistory'
  | 'nav.forms'
  | 'nav.formHistory'
  | 'nav.formMenu'
  | 'nav.others'
  | 'nav.extraServiceAndProduct'
  | 'nav.events'
  | 'nav.marketing'
  | 'nav.menu'
  | 'nav.drivers'
  | 'nav.deliverySection'
  | 'nav.orders'
  | 'nav.deliveryTakeaway'
  | 'nav.orderingConfiguration'
  | 'nav.delivery'
  | 'nav.takeaway'
  | 'nav.operations'
  | 'nav.suppliers'
  | 'nav.supplierOrders'
  | 'nav.supplierOrderHistory'
  | 'nav.ingredients'
  | 'nav.menuList'
  | 'nav.bulkMessage'
  | 'nav.automation'
  | 'nav.automationConfiguration'
  | 'nav.automationDashboard'
  | 'nav.surveys'
  | 'nav.dataCollectionForm'
  | 'nav.promotion'
  | 'nav.discountCards'
  | 'nav.cards'
  | 'nav.experienceCards'
  | 'nav.prizes'
  | 'nav.fidelity'
  | 'giftCards.title'
  | 'nav.landingPage'
  | 'nav.tag'
  | 'nav.publicRelation'
  | 'nav.myCustomers'
  | 'nav.staff'
  | 'nav.collaboratorRoles'
  | 'nav.collaborator'
  | 'nav.collaboratorReport'
  | 'nav.payments'
  | 'nav.settings'
  | 'nav.localConfiguration'
  | 'nav.stripeConfiguration'
  | 'nav.stripeTransactions'
  | 'nav.configureWhatsApp'
  | 'nav.messages'
  | 'nav.officialWhatsAppChannel'
  | 'nav.templates'
  | 'nav.whatsAppOfficial'
  | 'nav.whatsAppOfficialConfigure'
  | 'nav.whatsAppOfficialTemplates'
  | 'nav.whatsAppOfficialInbox'
  | 'nav.notifications';

interface NavLinkItem {
  type: 'link';
  labelKey: NavLabelKey;
  to: string;
  icon: IconComponent;
}

interface NavChildLink {
  labelKey: NavLabelKey;
  to: string;
  icon: IconComponent;
}

interface NavChildGroup {
  labelKey: NavLabelKey;
  icon: IconComponent;
  children: NavChildLink[];
}

type NavChild = NavChildLink | NavChildGroup;

interface NavDropdownNavItem {
  icon: IconComponent;
  type: 'dropdown';
  labelKey: NavLabelKey;
  children: NavChild[];
}

type NavItem = NavLinkItem | NavDropdownNavItem;

const isChildGroup = (child: NavChild): child is NavChildGroup =>
  'children' in child;

const NAV_ITEMS: NavItem[] = [
  {
    type: 'link',
    labelKey: 'nav.dashboard',
    to: ROUTES.DASHBOARD,
    icon: NavHomeIcon,
  },
  {
    icon: NavCalendarIcon,
    type: 'dropdown',
    labelKey: 'nav.calendar',
    children: [
      {
        labelKey: 'nav.bookingHistory',
        to: ROUTES.BOOKING_HISTORY,
        icon: NavReservationsIcon,
      },
      {
        labelKey: 'nav.forms',
        to: ROUTES.FORM_HISTORY,
        icon: NavFormIcon,
      },
      {
        labelKey: 'nav.others',
        icon: NavOthersIcon,
        children: [
          {
            labelKey: 'nav.events',
            to: ROUTES.EVENTS,
            icon: NavEventsIcon,
          },
          {
            labelKey: 'nav.formMenu',
            to: ROUTES.FORM_MENU,
            icon: NavMenuIcon,
          },
          {
            labelKey: 'nav.extraServiceAndProduct',
            to: ROUTES.EXTRA_SERVICE_AND_PRODUCT,
            icon: NavExtraServicesIcon,
          },
        ],
      },
    ],
  },
  {
    icon: NavMarketingIcon,
    type: 'dropdown',
    labelKey: 'nav.marketing',
    children: [
      {
        labelKey: 'nav.surveys',
        to: ROUTES.SURVEYS,
        icon: CircleHelp,
      },
      ...(FEATURES.AUTOMATION
        ? [
            {
              labelKey: 'nav.automation' as const,
              icon: Workflow,
              children: [
                {
                  labelKey: 'nav.automationConfiguration' as const,
                  to: ROUTES.AUTOMATION_CONFIGURATION,
                  icon: Settings2,
                },
                {
                  labelKey: 'nav.automationDashboard' as const,
                  to: ROUTES.AUTOMATION_DASHBOARD,
                  icon: ChartNoAxesCombined,
                },
              ],
            },
          ]
        : []),
      {
        labelKey: 'nav.cards' as const,
        icon: NavFidelityIcon,
        children: [
          {
            labelKey: 'nav.discountCards' as const,
            to: ROUTES.DISCOUNT_CARDS,
            icon: NavFidelityIcon,
          },
          {
            labelKey: 'nav.experienceCards' as const,
            to: ROUTES.FIDELITY_PRIZE_CATALOG.replace(':kind', 'experience'),
            icon: NavFidelityIcon,
          },
          {
            labelKey: 'nav.prizes' as const,
            to: ROUTES.FIDELITY_PRIZE_CATALOG.replace(':kind', 'gift'),
            icon: NavFidelityIcon,
          },
          {
            labelKey: 'giftCards.title' as const,
            to: ROUTES.GIFT_CARDS,
            icon: NavFidelityIcon,
          },
        ],
      },
      {
        labelKey: 'nav.fidelity',
        to: ROUTES.FIDELITY,
        icon: NavFidelityIcon,
      },
      {
        labelKey: 'nav.myCustomers',
        to: ROUTES.MY_CUSTOMERS,
        icon: NavMyClientsIcon,
      },
      {
        labelKey: 'nav.landingPage',
        to: ROUTES.LANDING_PAGES,
        icon: NavLandingPagesIcon,
      },
      {
        labelKey: 'nav.bulkMessage',
        to: ROUTES.BULK_MESSAGE,
        icon: NavMassiveDispatchIcon,
      },
      {
        labelKey: 'nav.promotion',
        to: ROUTES.PROMOTIONS,
        icon: NavPromotionsIcon,
      },
      {
        labelKey: 'nav.dataCollectionForm',
        to: ROUTES.DATA_COLLECTION_FORMS,
        icon: NavFormDataCollectionIcon,
      },
      {
        labelKey: 'nav.publicRelation',
        to: ROUTES.PUBLIC_RELATIONS,
        icon: NavPublicRelationIcon,
      },
      {
        labelKey: 'nav.tag',
        to: ROUTES.TAGS,
        icon: NavTagIcon,
      },
    ],
  },
  {
    type: 'dropdown',
    labelKey: 'nav.menu',
    icon: NavMenuIcon,
    children: [
      { labelKey: 'nav.menuList', to: ROUTES.MENU, icon: NavMenuIcon },
      {
        labelKey: 'nav.ingredients',
        to: '/menu/ingredients',
        icon: NavExtraServicesIcon,
      },
    ],
  },
  {
    type: 'dropdown',
    labelKey: 'nav.orders',
    icon: Truck,
    children: [
      { labelKey: 'nav.orders', icon: Truck, to: ROUTES.SUPPLIER_ORDERS },
      {
        labelKey: 'nav.suppliers',
        icon: NavCollaboratorsIcon,
        to: ROUTES.SUPPLIERS,
      },
      {
        labelKey: 'nav.supplierOrderHistory',
        icon: NavReportCollaboratorsIcon,
        to: ROUTES.SUPPLIER_ORDER_HISTORY,
      },
    ],
  },
  {
    type: 'dropdown',
    labelKey: 'nav.deliverySection',
    icon: NavDeliveryRiderIcon,
    children: [
      {
        labelKey: 'nav.deliveryTakeaway',
        icon: NavDeliveryRiderIcon,
        to: ROUTES.DELIVERY_ORDERS,
      },
      {
        labelKey: 'nav.supplierOrderHistory',
        icon: NavReportCollaboratorsIcon,
        to: ROUTES.ORDER_HISTORY,
      },
      {
        labelKey: 'nav.orderingConfiguration',
        icon: NavSettingsIcon,
        to: ROUTES.ORDERING_CONFIGURATION,
      },
      {
        labelKey: 'nav.drivers',
        icon: NavCollaboratorsIcon,
        to: ROUTES.ORDERING_DRIVERS,
      },
    ],
  },
  {
    icon: NavSettingsIcon,
    type: 'dropdown',
    labelKey: 'nav.settings',
    children: [
      {
        labelKey: 'nav.localConfiguration',
        to: ROUTES.SETTINGS_LOCAL_CONFIGURATION,
        icon: NavLocalConfigurationIcon,
      },
      {
        labelKey: 'nav.configureWhatsApp',
        to: ROUTES.SETTINGS_CONFIGURE_WHATSAPP,
        icon: NavConfigureWhatsAppIcon,
      },
      {
        labelKey: 'nav.messages',
        to: ROUTES.SETTINGS_MESSAGES,
        icon: NavMessagesIcon,
      },
      {
        labelKey: 'nav.staff',
        icon: NavStaffIcon,
        children: [
          {
            labelKey: 'nav.collaborator',
            to: ROUTES.COLLABORATORS,
            icon: NavCollaboratorsIcon,
          },
          {
            labelKey: 'nav.collaboratorReport',
            to: ROUTES.COLLABORATOR_REPORTS,
            icon: NavReportCollaboratorsIcon,
          },
        ],
      },
      {
        labelKey: 'nav.payments',
        icon: NavPaymentsIcon,
        children: [
          {
            labelKey: 'nav.stripeConfiguration',
            to: ROUTES.SETTINGS_STRIPE_CONFIGURATION,
            icon: NavStripeConfigurationIcon,
          },
          {
            labelKey: 'nav.stripeTransactions',
            to: ROUTES.SETTINGS_STRIPE_TRANSACTIONS,
            icon: NavStripeTransactionsIcon,
          },
        ],
      },
      {
        labelKey: 'nav.whatsAppOfficial',
        icon: NavWhatsAppOfficialIcon,
        children: [
          {
            labelKey: 'nav.whatsAppOfficialConfigure',
            to: ROUTES.SETTINGS_OFFICIAL_WHATSAPP_CHANNEL,
            icon: NavOfficialWhatsAppChannelIcon,
          },
          {
            labelKey: 'nav.whatsAppOfficialTemplates',
            to: ROUTES.SETTINGS_TEMPLATES,
            icon: NavTemplatesIcon,
          },
          {
            labelKey: 'nav.whatsAppOfficialInbox',
            to: ROUTES.SETTINGS_WHATSAPP_OFFICIAL,
            icon: NavWhatsAppOfficialIcon,
          },
        ],
      },
    ],
  },
];

const linkClassName = (isActive: boolean) =>
  cn(
    'rounded-lg px-2 py-2 text-sm 2xl:text-base font-medium transition-colors lowercase first-letter:uppercase capitalize flex items-center gap-1.5 relative whitespace-nowrap',
    isActive
      ? 'text-primary after:absolute after:-bottom-3 after:left-0 after:border-b-2 after:border-primary after:rounded-none after:w-full'
      : 'text-surface-muted hover:bg-surface-hover hover:text-white',
  );

const mobileLinkClassName = (isActive: boolean) =>
  cn(
    'flex items-center gap-2 rounded-lg px-3 py-2.5 text-sm font-medium',
    isActive
      ? 'bg-primary/15 text-primary'
      : 'text-surface-muted hover:bg-surface-hover hover:text-white',
  );

function flattenChildren(children: NavChild[]): NavChildLink[] {
  return children.flatMap((child) =>
    isChildGroup(child) ? child.children : [child],
  );
}

function getActiveSectionKey(pathname: string): NavLabelKey | null {
  const active = NAV_ITEMS.find(
    (item) =>
      item.type === 'dropdown' &&
      flattenChildren(item.children).some((child) =>
        pathname.startsWith(child.to),
      ),
  );
  return active ? active.labelKey : null;
}

function getActiveSubSectionKey(pathname: string): NavLabelKey | null {
  for (const item of NAV_ITEMS) {
    if (item.type !== 'dropdown') continue;

    const activeGroup = item.children
      .filter(isChildGroup)
      .find((group) =>
        group.children.some((child) => pathname.startsWith(child.to)),
      );

    if (activeGroup) return activeGroup.labelKey;
  }

  return null;
}

export function TopNavigation() {
  const { t, locale } = useTranslation();
  const compareLabels = (a: NavChild, b: NavChild) =>
    t(a.labelKey).localeCompare(t(b.labelKey), locale, { sensitivity: 'base' });
  const navItems = NAV_ITEMS.map((item) =>
    item.type === 'dropdown' && item.labelKey === 'nav.marketing'
      ? {
          ...item,
          children: item.children
            .map((child) =>
              isChildGroup(child)
                ? {
                    ...child,
                    children: [...child.children].sort(compareLabels),
                  }
                : child,
            )
            .sort(compareLabels),
        }
      : item,
  );
  const dispatch = useAppDispatch();
  const isMobileMenuOpen = useAppSelector(selectMobileMenuOpen);
  const { pathname } = useLocation();
  const destinations = NAV_ITEMS.flatMap((item) =>
    item.type === 'dropdown'
      ? flattenChildren(item.children).map((child) => child.to)
      : [item.to],
  );
  const linkEnd = (to: string) =>
    hasMoreSpecificNavigationMatch(pathname, to, destinations);

  const [openSection, setOpenSection] = useState<NavLabelKey | null>(() =>
    getActiveSectionKey(pathname),
  );
  const [openSubSection, setOpenSubSection] = useState<NavLabelKey | null>(() =>
    getActiveSubSectionKey(pathname),
  );

  useEffect(() => {
    if (!isMobileMenuOpen) {
      setOpenSection(getActiveSectionKey(pathname));
      setOpenSubSection(getActiveSubSectionKey(pathname));
    }
  }, [isMobileMenuOpen, pathname]);

  useEffect(() => {
    const mediaQuery = window.matchMedia('(min-width: 1280px)');
    const handleChange = () => {
      if (mediaQuery.matches) {
        dispatch(setMobileMenuOpen(false));
      }
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => {
      mediaQuery.removeEventListener('change', handleChange);
    };
  }, [dispatch]);

  useEffect(() => {
    if (!isMobileMenuOpen) return;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        dispatch(setMobileMenuOpen(false));
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isMobileMenuOpen, dispatch]);

  const closeMobileMenu = useCallback(() => {
    dispatch(setMobileMenuOpen(false));
  }, [dispatch]);

  const toggleFullscreen = useCallback(() => {
    if (!document.fullscreenElement) {
      void document.documentElement.requestFullscreen();
    } else {
      void document.exitFullscreen();
    }
  }, []);

  return (
    <>
      <header className="z-50 shrink-0 border-b bg-black shadow-nav">
        <div className="mx-auto flex min-h-16 items-center justify-between gap-3 px-4 py-2 xl:px-6">
          <div className="flex min-w-0 flex-1 items-center gap-3 2xl:gap-4">
            <NavLink
              to={ROUTES.DASHBOARD}
              className="flex shrink-0 items-center"
            >
              <img
                src={BRAND.LOGO_SRC}
                alt={BRAND.LOGO_ALT}
                className="h-9 w-auto object-contain 2xl:h-10"
              />
            </NavLink>

            <BranchIndicator />

            <nav
              className="hidden shrink-0 items-center xl:flex"
              aria-label="Main"
            >
              {navItems.map((item) =>
                item.type === 'link' ? (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    className={({ isActive }) => linkClassName(isActive)}
                  >
                    <Icon icon={item.icon} className="size-4 shrink-0" />
                    <span className="flex-1">{t(item.labelKey)}</span>
                  </NavLink>
                ) : (
                  <NavDropdown
                    key={item.labelKey}
                    compact
                    icon={item.icon}
                    label={t(item.labelKey)}
                    items={item.children.map((child) =>
                      isChildGroup(child)
                        ? {
                            label: t(child.labelKey),
                            icon: child.icon,
                            children: child.children.map((grandChild) => ({
                              label: t(grandChild.labelKey),
                              to: grandChild.to,
                              icon: grandChild.icon,
                            })),
                          }
                        : {
                            label: t(child.labelKey),
                            to: child.to,
                            icon: child.icon,
                          },
                    )}
                  />
                ),
              )}
            </nav>
          </div>

          <div className="flex shrink-0 items-center gap-1.5">
            <LanguageSelector variant="dark" />
            <ThemeToggle variant="dark" />
            <button
              type="button"
              onClick={toggleFullscreen}
              className="hidden h-9 w-9 items-center justify-center rounded-lg border border-surface-border text-surface-muted transition-colors hover:bg-surface-hover hover:text-white sm:flex"
              aria-label={t('header.fullscreen')}
            >
              <svg
                className="h-4 w-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4"
                />
              </svg>
            </button>
            <ProfileDropdown variant="dark" />

            <button
              type="button"
              className="rounded-lg p-2 text-surface-muted hover:bg-surface-hover hover:text-white xl:hidden"
              onClick={() => {
                dispatch(setMobileMenuOpen(!isMobileMenuOpen));
              }}
              aria-expanded={isMobileMenuOpen}
              aria-controls="mobile-nav-drawer"
              aria-label="Toggle menu"
            >
              <svg
                className="h-5 w-5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                {isMobileMenuOpen ? (
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                ) : (
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 6h16M4 12h16M4 18h16"
                  />
                )}
              </svg>
            </button>
          </div>
        </div>
      </header>

      <div
        className={cn(
          'fixed inset-0 z-40 bg-black/50 transition-opacity duration-300 xl:hidden',
          isMobileMenuOpen ? 'opacity-100' : 'pointer-events-none opacity-0',
        )}
        onClick={closeMobileMenu}
        aria-hidden={!isMobileMenuOpen}
      />

      <nav
        id="mobile-nav-drawer"
        className={cn(
          'fixed inset-y-0 right-0 z-50 flex w-72 max-w-[85vw] flex-col border-l border-surface-border bg-surface shadow-xl transition-transform duration-300 xl:hidden',
          isMobileMenuOpen ? 'translate-x-0' : 'translate-x-full',
          !isMobileMenuOpen && 'pointer-events-none',
        )}
        aria-label="Mobile"
        aria-hidden={!isMobileMenuOpen}
      >
        <div className="flex h-16 shrink-0 items-center justify-end border-b border-surface-border px-4">
          <button
            type="button"
            className="rounded-lg p-2 text-surface-muted transition-colors hover:bg-surface-hover hover:text-white"
            onClick={closeMobileMenu}
            aria-label="Close menu"
          >
            <svg
              className="h-5 w-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-4 py-3">
          <div className="space-y-0.5">
            {navItems.map((item) =>
              item.type === 'link' ? (
                <NavLink
                  key={`mobile-${item.to}`}
                  to={item.to}
                  onClick={closeMobileMenu}
                  className={({ isActive }) => mobileLinkClassName(isActive)}
                >
                  <Icon icon={item.icon} className="size-4 shrink-0" />
                  <span>{t(item.labelKey)}</span>
                </NavLink>
              ) : (
                <div key={`mobile-${item.labelKey}`}>
                  <button
                    type="button"
                    onClick={() => {
                      setOpenSection(
                        openSection === item.labelKey ? null : item.labelKey,
                      );
                    }}
                    aria-expanded={openSection === item.labelKey}
                    className="flex w-full items-center gap-2 rounded-lg px-3 py-2.5 text-sm font-semibold uppercase tracking-wide text-surface-muted transition-colors hover:bg-surface-hover hover:text-white"
                  >
                    <Icon icon={item.icon} className="size-4 shrink-0" />
                    <span className="min-w-0 flex-1 text-left">
                      {t(item.labelKey)}
                    </span>
                    <ChevronDown
                      className={cn(
                        'size-4 shrink-0 transition-transform duration-200',
                        openSection === item.labelKey && 'rotate-180',
                      )}
                    />
                  </button>

                  {openSection === item.labelKey && (
                    <div className="mt-0.5 space-y-0.5 pb-1">
                      {item.children.map((child) =>
                        isChildGroup(child) ? (
                          <div key={`mobile-${child.labelKey}`}>
                            <button
                              type="button"
                              onClick={() => {
                                setOpenSubSection(
                                  openSubSection === child.labelKey
                                    ? null
                                    : child.labelKey,
                                );
                              }}
                              aria-expanded={openSubSection === child.labelKey}
                              className="flex w-full items-center gap-2 rounded-lg py-2.5 pl-8 pr-3 text-sm font-medium text-surface-muted transition-colors hover:bg-surface-hover hover:text-white"
                            >
                              <Icon
                                icon={child.icon}
                                className="size-4 shrink-0"
                              />
                              <span className="min-w-0 flex-1 text-left">
                                {t(child.labelKey)}
                              </span>
                              <ChevronDown
                                className={cn(
                                  'size-4 shrink-0 transition-transform duration-200',
                                  openSubSection === child.labelKey &&
                                    'rotate-180',
                                )}
                              />
                            </button>

                            {openSubSection === child.labelKey && (
                              <div className="mt-0.5 space-y-0.5 pb-1">
                                {child.children.map((grandChild) => (
                                  <NavLink
                                    key={`mobile-${grandChild.to}`}
                                    to={grandChild.to}
                                    end={linkEnd(grandChild.to)}
                                    onClick={closeMobileMenu}
                                    className={({ isActive }) =>
                                      cn(mobileLinkClassName(isActive), 'pl-12')
                                    }
                                  >
                                    <Icon
                                      icon={grandChild.icon}
                                      className="size-4 shrink-0"
                                    />
                                    <span>{t(grandChild.labelKey)}</span>
                                  </NavLink>
                                ))}
                              </div>
                            )}
                          </div>
                        ) : (
                          <NavLink
                            key={`mobile-${child.to}`}
                            to={child.to}
                            end={linkEnd(child.to)}
                            onClick={closeMobileMenu}
                            className={({ isActive }) =>
                              cn(mobileLinkClassName(isActive), 'pl-8')
                            }
                          >
                            <Icon
                              icon={child.icon}
                              className="size-4 shrink-0"
                            />
                            <span>{t(child.labelKey)}</span>
                          </NavLink>
                        ),
                      )}
                    </div>
                  )}
                </div>
              ),
            )}
          </div>
        </div>
      </nav>
    </>
  );
}
